import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useLocation } from 'wouter';
import { ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Music2, X, Pencil, FileText, Play, GripVertical, Copy } from 'lucide-react';
import { Layout } from '../components/Layout';
import { api } from '../lib/api';

export function SetlistDetailPage() {
  const params = useParams<{ bandId: string; setlistId: string }>();
  const bandId = Number(params.bandId);
  const setlistId = Number(params.setlistId);
  const [, navigate] = useLocation();
  const [showAddSong, setShowAddSong] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const queryClient = useQueryClient();

  // Drag state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const { data: band } = useQuery({
    queryKey: ['band', bandId],
    queryFn: () => api.bands.get(bandId),
  });

  const canManage = band?.role === 'owner' || band?.role === 'admin';

  const { data: setlist, isLoading } = useQuery({
    queryKey: ['setlist', setlistId],
    queryFn: () => api.setlists.get(setlistId),
  });

  const { data: songs = [] } = useQuery({
    queryKey: ['songs', bandId],
    queryFn: () => api.songs.list(bandId),
  });

  const addItem = useMutation({
    mutationFn: (songId: number) => api.setlists.addItem(setlistId, songId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['setlist', setlistId] });
      setShowAddSong(false);
    },
  });

  const removeItem = useMutation({
    mutationFn: (itemId: number) => api.setlists.removeItem(setlistId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['setlist', setlistId] });
    },
  });

  const reorderItems = useMutation({
    mutationFn: (itemIds: number[]) => api.setlists.reorder(setlistId, itemIds),
    onError: () => {
      // On failure, refetch to restore correct server order
      queryClient.invalidateQueries({ queryKey: ['setlist', setlistId] });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['setlist', setlistId] });
    },
  });

  const updateSetlist = useMutation({
    mutationFn: (data: any) => api.setlists.update(setlistId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['setlist', setlistId] });
      setEditingName(false);
    },
  });

  const duplicateSetlist = useMutation({
    mutationFn: () => api.setlists.duplicate(setlistId),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['setlists', bandId] });
      navigate(`/bands/${bandId}/setlists/${data.id}`);
    },
  });

  const items: any[] = setlist?.items || [];
  const songIdsInSetlist = items.map((i: any) => i.songId);
  const availableSongs = songs.filter((s: any) => !songIdsInSetlist.includes(s.id));

  // ─── Reorder helpers ────────────────────────────────────────────────────────

  // Optimistically update the query cache so the UI reflects the new order
  // immediately, then persist to server. On error the server refetch restores.
  const applyReorder = (newItems: any[]) => {
    queryClient.setQueryData(['setlist', setlistId], (old: any) =>
      old ? { ...old, items: newItems } : old
    );
    reorderItems.mutate(newItems.map((i: any) => i.itemId));
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    applyReorder(newItems);
  };

  // ─── Drag-and-drop handlers ──────────────────────────────────────────────────

  const handleDragStart = (e: React.DragEvent, idx: number) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverIndex !== idx) setDragOverIndex(idx);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, toIdx: number) => {
    e.preventDefault();
    const fromIdx = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);
    if (fromIdx === null || fromIdx === toIdx) return;
    const newItems = [...items];
    const [dragged] = newItems.splice(fromIdx, 1);
    newItems.splice(toIdx, 0, dragged);
    applyReorder(newItems);
  };

  // ─── Chart links ─────────────────────────────────────────────────────────────

  const getChartLink = (item: any) => {
    if (item.personalVariantId) return `/editor/personal/${item.personalVariantId}`;
    if (item.baseChartId) return `/editor/base/${item.baseChartId}`;
    return `/bands/${bandId}/songs/${item.songId}`;
  };

  const gigPacketUrl = (() => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.personalVariantId) {
        return `/editor/personal/${item.personalVariantId}?setlist=${setlistId}&pos=${i}`;
      }
      if (item.baseChartId) {
        return `/editor/base/${item.baseChartId}?setlist=${setlistId}&pos=${i}`;
      }
    }
    return null;
  })();

  // ─── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Layout bandId={bandId} bandName={band?.name} title="Setlist">
        <div className="text-center py-12 text-gray-400">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout bandId={bandId} bandName={band?.name} title={setlist?.name || 'Setlist'}>
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Link href={`/bands/${bandId}/setlists`}>
            <button className="p-2 hover:bg-surface-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
          </Link>
          <div className="flex-1">
            {editingName && canManage ? (
              <form onSubmit={e => { e.preventDefault(); updateSetlist.mutate({ name: newName }); }} className="flex items-center gap-2">
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  className="bg-surface-800 border border-surface-700 rounded-lg px-3 py-1.5 text-lg font-bold focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                  required
                />
                <button type="submit" className="text-sm text-primary-400 hover:text-primary-300">Save</button>
                <button type="button" onClick={() => setEditingName(false)} className="text-sm text-gray-500 hover:text-gray-300">Cancel</button>
              </form>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold">{setlist?.name}</h2>
                {canManage && (
                  <button onClick={() => { setNewName(setlist?.name || ''); setEditingName(true); }} className="p-1 hover:bg-surface-800 rounded transition-colors">
                    <Pencil className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                )}
              </div>
            )}
            {setlist?.eventDate && (
              <p className="text-xs text-gray-500 mt-0.5">
                {new Date(setlist.eventDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>
          {canManage && (
            <button
              onClick={() => duplicateSetlist.mutate()}
              disabled={duplicateSetlist.isPending}
              className="shrink-0 flex items-center gap-2 bg-surface-800 hover:bg-surface-700 disabled:opacity-50 text-gray-300 font-medium py-2 px-3 rounded-lg transition-colors text-sm"
              title="Duplicate setlist"
            >
              <Copy className="w-4 h-4" />
              Duplicate
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={() => gigPacketUrl && navigate(gigPacketUrl)}
              disabled={!gigPacketUrl}
              className="shrink-0 flex items-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
              title={gigPacketUrl ? 'Open Gig Packet' : 'No charts available in this setlist'}
            >
              <Play className="w-4 h-4" />
              Open Gig Packet
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12">
            <Music2 className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 mb-4">No songs in this setlist yet.</p>
            {canManage && (
              <button
                onClick={() => setShowAddSong(true)}
                className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Songs
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2 mb-4">
            {items.map((item: any, idx: number) => {
              const isDragging = dragIndex === idx;
              const isDropTarget = dragOverIndex === idx && dragIndex !== null && dragIndex !== idx;
              return (
                <div
                  key={item.itemId}
                  draggable={canManage}
                  onDragStart={canManage ? (e) => handleDragStart(e, idx) : undefined}
                  onDragOver={canManage ? (e) => handleDragOver(e, idx) : undefined}
                  onDragEnd={canManage ? handleDragEnd : undefined}
                  onDrop={canManage ? (e) => handleDrop(e, idx) : undefined}
                  className={[
                    'flex items-center gap-3 p-3 bg-surface-900 border rounded-xl group transition-all duration-150',
                    isDragging
                      ? 'opacity-40 scale-[0.98] border-surface-600 shadow-none'
                      : isDropTarget
                        ? 'border-primary-500 bg-primary-600/10 shadow-lg shadow-primary-900/20'
                        : 'border-surface-700 hover:border-surface-600',
                  ].join(' ')}
                >
                  {/* Drag handle — visible to canManage users on hover */}
                  {canManage && (
                    <span className="shrink-0 cursor-grab active:cursor-grabbing text-gray-600 group-hover:text-gray-400 transition-colors opacity-0 group-hover:opacity-100 touch-none select-none">
                      <GripVertical className="w-4 h-4" />
                    </span>
                  )}

                  <span className="text-sm font-mono text-gray-500 w-6 text-right shrink-0">{idx + 1}</span>

                  {canManage && (
                    <div className="flex flex-col gap-0.5 shrink-0">
                      <button
                        onClick={() => moveItem(idx, -1)}
                        disabled={idx === 0}
                        className="p-0.5 hover:bg-surface-700 rounded disabled:opacity-20 transition-colors"
                      >
                        <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                      <button
                        onClick={() => moveItem(idx, 1)}
                        disabled={idx === items.length - 1}
                        className="p-0.5 hover:bg-surface-700 rounded disabled:opacity-20 transition-colors"
                      >
                        <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                      </button>
                    </div>
                  )}

                  <Link href={getChartLink(item)} className="flex-1 min-w-0">
                    <div className="cursor-pointer">
                      <span className="text-sm font-medium hover:text-primary-300 transition-colors block truncate">
                        {item.songTitle}
                      </span>
                      <div className="flex items-center gap-2 mt-0.5">
                        {item.songArtist && <span className="text-xs text-gray-500 truncate">{item.songArtist}</span>}
                        {item.defaultKey && <span className="text-xs text-gray-600">Key: {item.defaultKey}</span>}
                        {item.defaultTempo && <span className="text-xs text-gray-600">{item.defaultTempo} BPM</span>}
                      </div>
                    </div>
                  </Link>

                  <div className="flex items-center gap-1 shrink-0">
                    {item.baseChartId ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-surface-700 text-gray-400 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {item.personalVariantId ? 'My Version' : 'Base'}
                      </span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-surface-800 text-gray-500">No chart</span>
                    )}
                    {canManage && (
                      <button
                        onClick={() => removeItem.mutate(item.itemId)}
                        className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-red-900/30 rounded-lg transition-all"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {items.length > 0 && canManage && (
          <button
            onClick={() => setShowAddSong(true)}
            className="flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Song
          </button>
        )}

        {showAddSong && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddSong(false)}>
            <div className="bg-surface-900 border border-surface-700 rounded-xl w-full max-w-md mx-4 max-h-[60vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-4 border-b border-surface-700">
                <h3 className="font-semibold">Add Song to Setlist</h3>
                <button onClick={() => setShowAddSong(false)} className="p-1 hover:bg-surface-700 rounded">
                  <X className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              <div className="overflow-y-auto p-2 flex-1">
                {availableSongs.length === 0 ? (
                  <p className="text-sm text-gray-500 p-4 text-center">All songs are already in the setlist.</p>
                ) : (
                  availableSongs.map((song: any) => (
                    <button
                      key={song.id}
                      onClick={() => addItem.mutate(song.id)}
                      disabled={addItem.isPending}
                      className="flex items-center gap-3 w-full p-3 text-left hover:bg-surface-800 rounded-lg transition-colors"
                    >
                      <Music2 className="w-4 h-4 text-gray-500 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-medium block truncate">{song.title}</span>
                        {song.artist && <span className="text-xs text-gray-500">{song.artist}</span>}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
